import React, { useState, useEffect } from 'react';
import { Box, TextField, Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel, Checkbox } from '@mui/material';
import { useIntl } from 'react-intl';
import Api from '../../services/api';

const ENTITY_TYPES = ['CALLED', 'CHAT', 'USER', 'ORGANIZATION'];
const EXTERNAL_ENTITY_TYPES = ['ISSUE', 'DEAL', 'LEAD', 'CONTACT', 'COMPANY', 'TASK', 'RESERVATION', 'CLIENT', 'CHAT', 'USER', 'ORDER'];

const ExternalEntityTypeField = ({ value, onChange, label }) => {
  const intl = useIntl();
  const tr = (id, values) => intl.formatMessage({ id }, values);
  const finalLabel = label || tr('appconnector.acf.external.entity.type');
  const isCustom = value && !EXTERNAL_ENTITY_TYPES.includes(value);
  const [customMode, setCustomMode] = React.useState(isCustom);

  return (
    <Box>
      {!customMode ? (
        <TextField select size="small" label={finalLabel} fullWidth SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
          value={value || ''} onChange={e => onChange(e.target.value)}>
          <option value="">{tr('appconnector.acf.select')}</option>
          {EXTERNAL_ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </TextField>
      ) : (
        <TextField size="small" label={finalLabel} fullWidth value={value || ''} placeholder={tr('appconnector.acf.external.entity.placeholder')}
          InputLabelProps={{ shrink: true }} onChange={e => onChange(e.target.value.toUpperCase())} />
      )}
      <FormControlLabel
        control={<Checkbox size="small" checked={customMode} onChange={e => { setCustomMode(e.target.checked); if (!e.target.checked) onChange(''); }} />}
        label={<span style={{ fontSize: '12px', color: '#888' }}>{tr('appconnector.acf.other.value')}</span>}
        sx={{ mt: 0.5 }}
      />
    </Box>
  );
};

const ActionConfigForm = ({ actionCode, config, onChange, providerFields = [], statuses = [], automations = [] }) => {
  const intl = useIntl();
  const tr = (id, values) => intl.formatMessage({ id }, values);
  const [statusList, setStatusList] = useState(statuses || []);
  const [automationList, setAutomationList] = useState(automations || []);

  useEffect(() => {
    // Load statuses if not provided via props and action needs them
    if (['CHANGE_STATUS_CALLED', 'CLOSE_CALLED'].includes(actionCode) && statusList.length === 0) {
      Api.get('/statuses').then(result => {
        if (result.data && Array.isArray(result.data)) {
          setStatusList(result.data);
        } else if (result.data && result.data.data && Array.isArray(result.data.data)) {
          setStatusList(result.data.data);
        }
      }).catch(() => {});
    }
    // Load automations if needed
    if (actionCode === 'RUN_AUTOMATION' && automationList.length === 0) {
      Api.get('/automations').then(result => {
        if (result.data && Array.isArray(result.data)) {
          setAutomationList(result.data);
        } else if (result.data && result.data.data && Array.isArray(result.data.data)) {
          setAutomationList(result.data.data);
        }
      }).catch(() => {});
    }
  }, [actionCode]);

  const updateConfig = (key, value) => {
    const newConfig = { ...parsedConfig, [key]: value };
    onChange(JSON.stringify(newConfig));
  };

  const updateNestedConfig = (parentKey, childKey, value) => {
    const parent = parsedConfig[parentKey] || {};
    const newParent = { ...parent, [childKey]: value };
    const newConfig = { ...parsedConfig, [parentKey]: newParent };
    onChange(JSON.stringify(newConfig));
  };

  const parsedConfig = typeof config === 'string' ? (() => { try { return JSON.parse(config); } catch { return {}; } })() : (config || {});

  const renderEntityReferenceFields = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, background: '#f8faf5', borderRadius: '8px', border: '1px solid #e8f5e9' }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#2e7d32' }}>{tr('appconnector.acf.section.entity.ref')}</div>
      <TextField select size="small" label={tr('appconnector.acf.jsonpath.external.id')} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
        value={parsedConfig.entity_reference?.external_id_field_path || ''}
        onChange={e => updateNestedConfig('entity_reference', 'external_id_field_path', e.target.value)}>
        <option value="">{tr('appconnector.acf.select.or.type')}</option>
        {providerFields.map(f => <option key={f.id} value={f.fieldPath}>{f.fieldName} ({f.fieldPath})</option>)}
      </TextField>
      <ExternalEntityTypeField
        value={parsedConfig.entity_reference?.external_entity_type || ''}
        onChange={v => updateNestedConfig('entity_reference', 'external_entity_type', v)}
      />
      <TextField select size="small" label={tr('appconnector.acf.internal.entity.type')} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
        value={parsedConfig.entity_reference?.internal_entity_type || ''}
        onChange={e => updateNestedConfig('entity_reference', 'internal_entity_type', e.target.value)}>
        {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
      </TextField>
    </Box>
  );

  const renderMatchFields = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, background: '#e3f2fd', borderRadius: '8px', border: '1px solid #bbdefb' }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#1565c0' }}>{tr('appconnector.acf.section.match')}</div>
      <TextField select size="small" label={tr('appconnector.acf.jsonpath.external.id')} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
        value={parsedConfig.match_external_id_field_path || ''}
        onChange={e => onChange(JSON.stringify({ ...parsedConfig, match_external_id_field_path: e.target.value }))}>
        <option value="">{tr('appconnector.acf.select')}</option>
        {providerFields.map(f => <option key={f.id} value={f.fieldPath}>{f.fieldName} ({f.fieldPath})</option>)}
      </TextField>
      <ExternalEntityTypeField
        value={parsedConfig.match_external_entity_type || ''}
        onChange={v => onChange(JSON.stringify({ ...parsedConfig, match_external_entity_type: v }))}
      />
    </Box>
  );

  switch (actionCode) {
    case 'CREATE_CALLED':
    case 'CREATE_CHAT':
    case 'CREATE_USER':
    case 'CREATE_ORGANIZATION':
      return renderEntityReferenceFields();

    case 'UPDATE_CALLED':
    case 'ADD_ACCOMPANIMENT':
    case 'ADD_TAG':
    case 'ADD_CALLED_ATTACHMENT':
    case 'ADD_CHAT_MESSAGE':
    case 'ASSIGN_CHAT':
    case 'CHANGE_STATUS_CHAT':
    case 'CLOSE_CHAT':
    case 'UPDATE_USER':
      return renderMatchFields();

    case 'CHANGE_STATUS_CALLED':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {renderMatchFields()}
          <TextField select size="small" label={tr('appconnector.acf.status.strategy')} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
            value={parsedConfig.target_status_strategy || 'FROM_MAPPING'}
            onChange={e => onChange(JSON.stringify({ ...parsedConfig, target_status_strategy: e.target.value }))}>
            <option value="FROM_MAPPING">{tr('appconnector.acf.strategy.from.mapping')}</option>
            <option value="FIXED">{tr('appconnector.acf.strategy.fixed')}</option>
          </TextField>
          {parsedConfig.target_status_strategy === 'FIXED' && (
            <TextField select size="small" label={tr('appconnector.acf.status')} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
              value={parsedConfig.target_status_id || ''}
              onChange={e => onChange(JSON.stringify({ ...parsedConfig, target_status_id: parseInt(e.target.value) || null }))}>
              <option value="">{tr('appconnector.acf.select.status')}</option>
              {statusList.map(s => <option key={s.id} value={s.id}>{s.name || s.statusName || tr('appconnector.acf.fallback.status', { id: s.id })}</option>)}
            </TextField>
          )}
        </Box>
      );

    case 'ASSIGN_CALLED':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {renderMatchFields()}
          <TextField select size="small" label={tr('appconnector.acf.assign.strategy')} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
            value={parsedConfig.assigned_user_strategy || 'FIXED'}
            onChange={e => onChange(JSON.stringify({ ...parsedConfig, assigned_user_strategy: e.target.value }))}>
            <option value="FIXED">{tr('appconnector.acf.assign.fixed.agent')}</option>
            <option value="LOOKUP_FROM_PAYLOAD">{tr('appconnector.acf.assign.from.payload')}</option>
          </TextField>
          {parsedConfig.assigned_user_strategy === 'FIXED' && (
            <TextField size="small" label={tr('appconnector.acf.agent.id')} type="number"
              value={parsedConfig.assigned_user_id || ''}
              onChange={e => onChange(JSON.stringify({ ...parsedConfig, assigned_user_id: parseInt(e.target.value) || null }))} />
          )}
          {parsedConfig.assigned_user_strategy === 'LOOKUP_FROM_PAYLOAD' && (
            <>
              <TextField select size="small" label={tr('appconnector.acf.jsonpath.email')} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
                value={parsedConfig.assigned_user_field_path || ''}
                onChange={e => onChange(JSON.stringify({ ...parsedConfig, assigned_user_field_path: e.target.value }))}>
                <option value="">{tr('appconnector.acf.select')}</option>
                {providerFields.map(f => <option key={f.id} value={f.fieldPath}>{f.fieldName}</option>)}
              </TextField>
              <TextField size="small" label={tr('appconnector.acf.lookup.field')} value={parsedConfig.assigned_user_lookup_field || 'email'} placeholder="email"
                onChange={e => onChange(JSON.stringify({ ...parsedConfig, assigned_user_lookup_field: e.target.value }))} />
            </>
          )}
        </Box>
      );

    case 'CLOSE_CALLED':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {renderMatchFields()}
          <TextField select size="small" label={tr('appconnector.acf.close.status')} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
            value={parsedConfig.close_status_id || ''}
            onChange={e => onChange(JSON.stringify({ ...parsedConfig, close_status_id: parseInt(e.target.value) || null }))}>
            <option value="">{tr('appconnector.acf.select.status')}</option>
            {statusList.map(s => <option key={s.id} value={s.id}>{s.name || s.statusName || tr('appconnector.acf.fallback.status', { id: s.id })}</option>)}
          </TextField>
          <TextField select size="small" label={tr('appconnector.acf.accompaniment.text')} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
            value={parsedConfig.accompaniment_text_strategy || 'FIXED'}
            onChange={e => onChange(JSON.stringify({ ...parsedConfig, accompaniment_text_strategy: e.target.value }))}>
            <option value="FIXED">{tr('appconnector.acf.text.fixed')}</option>
            <option value="FROM_PAYLOAD">{tr('appconnector.acf.text.from.payload')}</option>
            <option value="TEMPLATE">{tr('appconnector.acf.text.template')}</option>
          </TextField>
          {parsedConfig.accompaniment_text_strategy === 'FIXED' && (
            <TextField size="small" label={tr('appconnector.acf.text')} multiline rows={2} value={parsedConfig.accompaniment_text || ''}
              onChange={e => onChange(JSON.stringify({ ...parsedConfig, accompaniment_text: e.target.value }))} />
          )}
          {parsedConfig.accompaniment_text_strategy === 'TEMPLATE' && (
            <TextField size="small" label={tr('appconnector.acf.template')} multiline rows={2} value={parsedConfig.accompaniment_text_template || ''}
              helperText={tr('appconnector.acf.template.helper')}
              onChange={e => onChange(JSON.stringify({ ...parsedConfig, accompaniment_text_template: e.target.value }))} />
          )}
        </Box>
      );

    case 'RUN_AUTOMATION':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField select size="small" label={tr('appconnector.acf.automation')} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
            value={parsedConfig.automation_id || ''}
            onChange={e => onChange(JSON.stringify({ ...parsedConfig, automation_id: parseInt(e.target.value) || null }))}>
            <option value="">{tr('appconnector.acf.select.automation')}</option>
            {automationList.map(a => <option key={a.id} value={a.id}>{a.name || a.title || tr('appconnector.acf.fallback.automation', { id: a.id })}</option>)}
          </TextField>
          {automationList.length === 0 && (
            <TextField size="small" label={tr('appconnector.acf.automation.id.fallback')} type="number"
              value={parsedConfig.automation_id || ''}
              onChange={e => onChange(JSON.stringify({ ...parsedConfig, automation_id: parseInt(e.target.value) || null }))} />
          )}
          {renderMatchFields()}
        </Box>
      );

    case 'CALL_WEBHOOK':
      return (
        <TextField size="small" label={tr('appconnector.acf.webhook.id')} type="number" fullWidth
          value={parsedConfig.webhook_id || ''}
          onChange={e => onChange(JSON.stringify({ ...parsedConfig, webhook_id: parseInt(e.target.value) || null }))} />
      );

    default:
      return (
        <TextField label={tr('appconnector.acf.config.json')} fullWidth multiline rows={4}
          value={typeof config === 'string' ? config : JSON.stringify(config || {}, null, 2)}
          onChange={e => onChange(e.target.value)}
          sx={{ fontFamily: 'monospace', fontSize: '12px' }} />
      );
  }
};

export default ActionConfigForm;
